import { startTransition, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDialog } from "@platform/store";
import { OptionType } from "@core/types/select.types";
import { OrganizationType } from "@platform/types/orgInvitation.types";
import { OrganizationOption } from "@core/features/organization/organization.types";
import { orgInvitationEventBus } from "@platform/events/orgInvitation.events";
import { usePlatformStore } from "@platform/store";
import { useValidateOrgLookupItem } from "@platform/features/invitation/hooks/useValidateLookupOrg";
import { OrgLookupItem } from "./OrgLookupItem";

/** Data for newly created (user-entered) organizations */
type CreatedOrgData = { created: true };

/** Type guard to check if data is from a created org */
function isCreatedOrg(data: unknown): data is CreatedOrgData {
  return (
    typeof data === "object" &&
    data !== null &&
    "created" in data &&
    (data as CreatedOrgData).created === true
  );
}

/** Transform OrganizationOption (from API) to OrganizationType (for validation) */
function toOrganizationType(org: OrganizationOption): OrganizationType {
  return {
    organization_id: org.id,
    organization_name: org.name,
    organization_nickname: org.nickname ?? "",
    // invitation_code is retrieved via validation, not from the org list
    invitation_code: "",
  };
}

export function OrgLookupItemContainer({ label, data }: OptionType) {
  const setInvitation = usePlatformStore((s) => s.setInvitation);

  const { closeDialog } = useDialog();

  const navigate = useNavigate();

  // Check if this is a newly created org (not from API)
  const isNew = isCreatedOrg(data);

  // Transform API data to OrganizationType for validation
  // Only transform if not a created org
  const organizationForValidation = useMemo<OrganizationType | null>(() => {
    if (isNew || !data) return null;
    return toOrganizationType(data as OrganizationOption);
  }, [data, isNew]);

  const {
    isLoading,
    isValid,
    data: validatedData,
  } = useValidateOrgLookupItem(organizationForValidation);

  const handleJoinClick = () => {
    const shortcode = validatedData?.invitation?.shortcode;
    if (!shortcode || !organizationForValidation) return;

    orgInvitationEventBus.emitEvent("lookupOrg:joinIntent", {
      organization: organizationForValidation,
    });

    closeDialog();

    startTransition(() => {
      setInvitation(validatedData.invitation, "lookup");

      setTimeout(() => {
        navigate(`/invitation/${shortcode}/join`);
      }, 100);
    });
  };

  const handleInviteOrg = () => {
    // For created orgs, we create a minimal organization object
    const organization: OrganizationType = organizationForValidation ?? {
      organization_id: 0,
      organization_name: label,
      organization_nickname: "",
      invitation_code: "",
    };

    orgInvitationEventBus.emitEvent("lookupOrg:inviteIntent", { organization });

    navigate("/invite-institution");
  };

  return (
    <OrgLookupItem
      handleJoinClick={handleJoinClick}
      handleInviteOrg={handleInviteOrg}
      isLoading={isLoading}
      isValid={isValid}
      isNew={isNew}
      label={label}
    />
  );
}

export default OrgLookupItemContainer;

